import { Kegiatan, KegiatanAbsen, Santri } from '../types';
import { set, getDay, isWithinInterval } from 'date-fns';

export const getKegiatanEndTime = (kegiatan: Kegiatan, referenceDate: Date = new Date()): Date | null => {
    const [endHour, endMinute] = kegiatan.jamAkhir.split(':').map(Number);
    const tutupAbsenMenit = Number(kegiatan.absenDitutupSetelah) || 0;

    let endTime: Date;

    if (kegiatan.jenis === 'Rutin') {
        endTime = set(referenceDate, { hours: endHour, minutes: endMinute, seconds: 0, milliseconds: 0 });
    } else {
        if (!kegiatan.tanggalAkhir) return null;
        endTime = set(new Date(kegiatan.tanggalAkhir), { hours: endHour, minutes: endMinute, seconds: 0, milliseconds: 0 });
    }

    return new Date(endTime.getTime() + tutupAbsenMenit * 60000);
}

const dayMap: { [key: string]: number } = {
  'Ahad': 0,
  'Senin': 1,
  'Selasa': 2,
  'Rabu': 3,
  'Kamis': 4,
  'Jumat': 5,
  'Sabtu': 6,
};

export const isKegiatanOpenForAbsen = (kegiatan: Kegiatan, now: Date = new Date()): boolean => {
  if (!kegiatan.isActive) return false;

  const [startHour, startMinute] = kegiatan.jamMulai.split(':').map(Number);
  const [endHour, endMinute] = kegiatan.jamAkhir.split(':').map(Number);

  const mulaiAbsenMenit = Number(kegiatan.absenBisaDimulaiSebelum) || 0;
  const tutupAbsenMenit = Number(kegiatan.absenDitutupSetelah) || 0;

  if (kegiatan.jenis === 'Rutin') {
    const todayDayName = ['Ahad', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'][getDay(now)];
    if (!kegiatan.hari?.includes(todayDayName)) {
      return false;
    }

    let startTime = set(now, { hours: startHour, minutes: startMinute, seconds: 0, milliseconds: 0 });
    startTime = new Date(startTime.getTime() - mulaiAbsenMenit * 60000);

    let endTime = set(now, { hours: endHour, minutes: endMinute, seconds: 0, milliseconds: 0 });
    endTime = new Date(endTime.getTime() + tutupAbsenMenit * 60000);

    return isWithinInterval(now, { start: startTime, end: endTime });
  }

  if (kegiatan.jenis === 'Non Rutin') {
    if (!kegiatan.tanggalMulai || !kegiatan.tanggalAkhir) return false;

    let effectiveStartTime = set(new Date(kegiatan.tanggalMulai), { hours: startHour, minutes: startMinute, seconds: 0, milliseconds: 0 });
    effectiveStartTime = new Date(effectiveStartTime.getTime() - mulaiAbsenMenit * 60000);

    let effectiveEndTime = set(new Date(kegiatan.tanggalAkhir), { hours: endHour, minutes: endMinute, seconds: 0, milliseconds: 0 });
    effectiveEndTime = new Date(effectiveEndTime.getTime() + tutupAbsenMenit * 60000);

    return isWithinInterval(now, { start: effectiveStartTime, end: effectiveEndTime });
  }

  return false;
};

export const getRelevantSantri = (kegiatan: Kegiatan, allSantri: Santri[]): Santri[] => {
  // Determine allowed genders based on location
  const allowedGenders: string[] = [];
  if (kegiatan.lokasi.includes('Putra')) allowedGenders.push('Laki-laki');
  if (kegiatan.lokasi.includes('Putri')) allowedGenders.push('Perempuan');

  return allSantri.filter(s => {
    // 1. Check Status
    if (s.status !== 'Aktif') return false;

    // 2. Check Gender (Location-based)
    if (allowedGenders.length > 0 && s.jenisKelamin && !allowedGenders.includes(s.jenisKelamin)) return false;

    // 3. Check Jenjang
    const isJenjangMatch = kegiatan.jenjang.some(jenjang => {
        if (jenjang === 'SMP') return /^[7-9]/.test(s.kelas);
        if (jenjang === 'SMA') return /^(X|XI|XII)/.test(s.kelas);
        return false;
    });
    if (!isJenjangMatch) return false;

    // 4. Check Target Peserta (Specific Class)
    if (kegiatan.targetPeserta === 'Kelas Tertentu' && kegiatan.kelasPeserta && kegiatan.kelasPeserta.length > 0) {
        if (!kegiatan.kelasPeserta.includes(s.kelas)) return false;
    }

    // 5. Check Exceptions (Pengecualian individu)
    if (kegiatan.exceptions?.santriIds) {
        // Hanya mode 'BukanPeserta' yang dikeluarkan dari daftar (bukan peserta).
        // mode 'Izin' (atau legacy tanpa mode) tetap peserta → di-auto-Izin oleh useKegiatanAutoPermission.
        if (kegiatan.exceptions.santriIds.some(ex => ex.id === s.id && ex.mode === 'BukanPeserta')) {
            return false;
        }
    }

    return true;
  });
};

/** True jika dua tanggal jatuh di hari kalender yang sama (zona waktu perangkat / WIB). */
export const isSameLocalDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** Semua record presensi satu kegiatan pada hari tertentu. */
export const getKegiatanRecordsForDate = (
  kegiatanId: string,
  records: KegiatanAbsen[],
  date: Date = new Date()
): KegiatanAbsen[] =>
  records.filter(r => r.kegiatanId === kegiatanId && isSameLocalDay(r.tanggal, date));

/**
 * Satu-satunya rumus progress presensi kegiatan, dipakai semua layar
 * (Tab Kegiatan, layar Absen Semua Santri, Tab Ringkasan & detailnya).
 *
 * Yang dihitung adalah jumlah SANTRI unik yang sudah punya record hari itu,
 * bukan jumlah record. Pada kegiatan Parenting satu santri bisa punya 2 record
 * (Ayah + Ibu) tapi tetap dihitung 1 kehadiran.
 */
export const getKegiatanProgress = (
  recordsForDate: KegiatanAbsen[],
  totalSantri: number,
  /**
   * Santri yang menjadi penyebut. Bila diisi, hanya record milik santri ini yang
   * dihitung, supaya pembilang & penyebut memakai populasi yang sama
   * (mis. akun pegawai yang dibatasi ke satu gender).
   */
  relevantSantriIds?: Set<string>
): { santriIds: Set<string>; santriCount: number; percent: number } => {
  const santriIds = new Set(
    recordsForDate
      .filter(r => !relevantSantriIds || relevantSantriIds.has(r.studentId))
      .map(r => r.studentId)
  );
  const santriCount = santriIds.size;
  const percent = totalSantri > 0 ? Math.min(100, (santriCount / totalSantri) * 100) : 0;
  return { santriIds, santriCount, percent };
};

// ---------------------------------------------------------------------------
// Tutup presensi (plan/PLAN_TUTUP_PRESENSI_KEGIATAN.md)
// Aturan yang sama disalin di functions/src/tutupPresensiKegiatan.ts —
// bila diubah di sini, sesuaikan juga di sana.
// ---------------------------------------------------------------------------

/** Petugas pada record Alpha/Izin yang dibuat saat presensi ditutup. */
export const PETUGAS_SISTEM = 'Sistem';

/** Tanggal 'YYYY-MM-DD' menurut WIB (sama dengan kolom kegiatan_absen.tanggal_wib). */
export const toWibDateStr = (date: Date): string => {
  const w = new Date(date.getTime() + 7 * 60 * 60 * 1000);
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${w.getUTCFullYear()}-${pad(w.getUTCMonth() + 1)}-${pad(w.getUTCDate())}`;
};

/** Jam tutup presensi untuk hari `ref`: jamAkhir + absenDitutupSetelah. */
export const getJamTutupHariIni = (kegiatan: Kegiatan, ref: Date = new Date()): Date | null => {
  if (!kegiatan.jamAkhir) return null;
  const [h, m] = kegiatan.jamAkhir.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  const end = set(ref, { hours: h, minutes: m, seconds: 0, milliseconds: 0 });
  return new Date(end.getTime() + (Number(kegiatan.absenDitutupSetelah) || 0) * 60000);
};

/**
 * Sebuah kelas/asrama dianggap "dipresensi" bila punya minimal satu record
 * Hadir/Sakit/Alpha hari itu. Record Izin tidak dihitung karena Izin juga
 * dibuat otomatis (izin aktif / pengecualian) tanpa guru melakukan presensi.
 */
export const isRecordPresensiGuru = (r: KegiatanAbsen): boolean => r.status !== 'Izin';

/** Kelas yang punya data presensi dari guru pada record yang diberikan. */
export const getKelasDipresensi = (recordsForDate: KegiatanAbsen[], santriList: Santri[]): Set<string> => {
  const kelasById = new Map(santriList.map(s => [s.id, s.kelas]));
  const out = new Set<string>();
  recordsForDate.forEach(r => {
    if (!isRecordPresensiGuru(r)) return;
    const kelas = kelasById.get(r.studentId) || r.kelas;
    if (kelas) out.add(kelas);
  });
  return out;
};
